// ABOUTME: Utility functions for importing database data from various formats (SQLite, CSV, JSON)
// ABOUTME: Handles data validation, duplicate detection, and batch insertion of imported records

import { DataItem } from '../../types';
import { StorageProvider } from './types';
import { generateId, isValidCompactUUID, isValidStandardUUID } from '../uuidUtils';

export type ImportFormat = 'sqlite' | 'csv' | 'json';

export interface ImportResult {
    imported: number;
    skipped: number;
    errors: string[];
}

export interface ImportOptions {
    skipDuplicates?: boolean; // Default: true
    updateExisting?: boolean; // Default: false
    validateData?: boolean;   // Default: true
}

/**
 * Import data into a storage provider from the specified format
 */
export async function importData(
    provider: StorageProvider, 
    file: File, 
    format: ImportFormat, 
    options: ImportOptions = {}
): Promise<ImportResult> {
    const opts = {
        skipDuplicates: true,
        updateExisting: false,
        validateData: true,
        ...options
    };

    console.log('Importing data:', { format, filename: file.name, size: file.size, options: opts });

    switch (format) {
        case 'json':
            return await importJSON(provider, file, opts);
        case 'csv':
            return await importCSV(provider, file, opts);
        case 'sqlite':
            return await importSQLite(provider, file, opts);
        default:
            throw new Error(`Unsupported import format: ${format}`);
    }
}

/**
 * Import data from JSON format
 */
async function importJSON(provider: StorageProvider, file: File, options: ImportOptions): Promise<ImportResult> {
    console.log('importJSON called');
    const result: ImportResult = { imported: 0, skipped: 0, errors: [] };

    try {
        const text = await file.text();
        const data = JSON.parse(text);

        if (!Array.isArray(data)) {
            throw new Error('JSON file must contain an array of records');
        }

        console.log(`Processing ${data.length} JSON records`);
        return await processItems(provider, data, options);
    } catch (error) {
        console.error('Error importing JSON:', error);
        result.errors.push(`JSON parsing error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return result;
    }
}

/**
 * Import data from CSV format
 */
async function importCSV(provider: StorageProvider, file: File, options: ImportOptions): Promise<ImportResult> {
    console.log('importCSV called');
    const result: ImportResult = { imported: 0, skipped: 0, errors: [] };

    try {
        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length === 0) {
            throw new Error('CSV file is empty');
        }

        const headers = parseCSVLine(lines[0]);
        console.log(`CSV headers:`, headers);

        const items: Partial<DataItem>[] = [];
        for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            if (values.length !== headers.length) {
                result.errors.push(`Line ${i + 1}: Column count mismatch`);
                continue;
            }

            const item: any = {};
            headers.forEach((header, index) => {
                const value = values[index];
                
                if (header.startsWith('data.')) {
                    // Handle flattened data fields
                    if (!item.data) item.data = {};
                    const dataKey = header.substring(5); // Remove 'data.' prefix
                    try {
                        item.data[dataKey] = JSON.parse(value);
                    } catch {
                        item.data[dataKey] = value;
                    }
                } else {
                    // Handle regular fields
                    if (header === 'timestamp' || header === 'id') {
                        item[header] = value;
                    } else {
                        item[header] = value;
                    }
                }
            });

            items.push(item);
        }

        console.log(`Processed ${items.length} CSV records`);
        return await processItems(provider, items, options);
    } catch (error) {
        console.error('Error importing CSV:', error);
        result.errors.push(`CSV parsing error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return result;
    }
}

/**
 * Import data from SQLite format
 * Note: This requires additional SQLite handling and is more complex
 */
async function importSQLite(provider: StorageProvider, file: File, options: ImportOptions): Promise<ImportResult> {
    console.log('importSQLite called');
    const result: ImportResult = { imported: 0, skipped: 0, errors: [] };

    try {
        // For now, we'll return an error since SQLite import requires more complex handling
        result.errors.push('SQLite import is not yet implemented. Please export as JSON or CSV instead.');
        return result;
    } catch (error) {
        console.error('Error importing SQLite:', error);
        result.errors.push(`SQLite import error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return result;
    }
}

/**
 * Process and validate items before insertion
 */
async function processItems(provider: StorageProvider, items: any[], options: ImportOptions): Promise<ImportResult> {
    const result: ImportResult = { imported: 0, skipped: 0, errors: [] };
    
    // Get existing IDs to check for duplicates
    const existingIds = new Set<string>();
    if (options.skipDuplicates || options.updateExisting) {
        try {
            // Get all existing items to check IDs
            let page = 1;
            const pageSize = 1000;
            let hasMore = true;
            
            while (hasMore) {
                const existingItems = await provider.getItems(page, pageSize);
                existingItems.forEach(item => existingIds.add(item.id));
                hasMore = existingItems.length === pageSize;
                page++;
            }
            
            console.log(`Found ${existingIds.size} existing records`);
        } catch (error) {
            console.warn('Could not fetch existing items for duplicate checking:', error);
        }
    }

    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        try {
            // Validate and normalize the item
            const normalizedItem = await validateAndNormalizeItem(item, options.validateData);
            
            // Check for duplicates
            if (existingIds.has(normalizedItem.id)) {
                if (options.updateExisting) {
                    // Update existing item
                    await provider.updateItem(normalizedItem.id, {
                        name: normalizedItem.name,
                        description: normalizedItem.description,
                        imageUrl: normalizedItem.imageUrl,
                        category: normalizedItem.category,
                        timestamp: normalizedItem.timestamp,
                        data: normalizedItem.data
                    });
                    result.imported++;
                } else {
                    // Skip duplicate
                    result.skipped++;
                }
            } else {
                // Add new item
                await provider.addItem({
                    name: normalizedItem.name,
                    description: normalizedItem.description,
                    imageUrl: normalizedItem.imageUrl,
                    category: normalizedItem.category,
                    timestamp: normalizedItem.timestamp,
                    data: normalizedItem.data
                });
                result.imported++;
            }
        } catch (error) {
            console.error(`Error processing item ${i + 1}:`, error);
            result.errors.push(`Item ${i + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    return result;
}

/**
 * Validate and normalize an item for import
 */
async function validateAndNormalizeItem(item: any, validate: boolean = true): Promise<DataItem> {
    if (validate) {
        // Validate required fields
        if (!item.name || typeof item.name !== 'string') {
            throw new Error('Missing or invalid name field');
        }
        
        if (!item.category || !['workout', 'meal', 'profile', 'workout_template', 'meal_template'].includes(item.category)) {
            throw new Error('Missing or invalid category field');
        }
    }

    // Ensure we have a valid ID
    let id = item.id;
    if (!id || typeof id !== 'string' || (!isValidCompactUUID(id) && !isValidStandardUUID(id))) {
        // Generate new ID if missing or invalid
        id = generateId();
    }

    // Ensure timestamp is a number
    let timestamp = item.timestamp;
    if (typeof timestamp === 'string') {
        timestamp = parseInt(timestamp, 10);
    }
    if (!timestamp || typeof timestamp !== 'number') {
        timestamp = Date.now();
    }

    return {
        id,
        name: item.name || 'Imported Item',
        description: item.description || '',
        imageUrl: item.imageUrl || '',
        category: item.category || 'workout',
        timestamp,
        data: item.data
    };
}

/**
 * Parse a CSV line handling quoted values and commas
 */
function parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
                // Escaped quote
                current += '"';
                i++; // Skip next quote
            } else {
                // Toggle quote mode
                inQuotes = !inQuotes;
            }
        } else if (char === ',' && !inQuotes) {
            // End of field
            result.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }
    
    // Add the last field
    result.push(current.trim());
    
    return result;
}